import React from "react";

import { Progress } from "@replo/design-system/components/progress/Progress";
import twMerge from "@replo/design-system/utils/twMerge";
import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { LoaderIcon } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

type LoadingPhase = "connecting" | "starting" | "loading" | "ready";

/**
 * Note (Nick, 2026-02-02): Determines loading phase based on reliable signals only.
 *
 * We use three reliable signals:
 * - isServerSandboxLoading: React Query state (is sandbox API call in progress?)
 * - url: Sandbox URL (undefined until sandbox is ready)
 * - loadedRoute: The route confirmed loaded by IFRAME_READY
 */
function useLoadingPhase(pageRoute: string): {
  phase: LoadingPhase;
  progress: number;
} {
  const { isServerSandboxLoading, url, loadedRoute } = useNextDevServer();

  const normalizedRoute = pageRoute.startsWith("/")
    ? pageRoute.slice(1)
    : pageRoute;
  const hasPageLoaded = loadedRoute === normalizedRoute;

  if (hasPageLoaded) {
    return { phase: "ready", progress: 100 };
  }
  if (url) {
    return { phase: "loading", progress: 75 };
  }
  if (isServerSandboxLoading) {
    return { phase: "connecting", progress: 15 };
  }
  return { phase: "starting", progress: 45 };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const useAnimatedProgress = (target: number, baseSpeed = 0.02) => {
  const [value, setValue] = React.useState(target);
  const maxProgressRef = React.useRef(target);
  const targetStabilizedAtRef = React.useRef<{
    target: number;
    time: number;
  } | null>(null);

  React.useEffect(() => {
    maxProgressRef.current = Math.max(maxProgressRef.current, target);

    if (
      !targetStabilizedAtRef.current ||
      targetStabilizedAtRef.current.target !== target
    ) {
      targetStabilizedAtRef.current = { target, time: Date.now() };
    }

    let frame: number;

    const animate = () => {
      setValue((v) => {
        let effectiveTarget = maxProgressRef.current;

        // Note (Nick, 2026-01-13, REPL-22167): If we've been stuck at a value < 100 for more than 500ms,
        // slowly creep forward to give the user feedback something's happening
        if (targetStabilizedAtRef.current && target < 100) {
          const timeStuck = Date.now() - targetStabilizedAtRef.current.time;
          if (timeStuck > 500) {
            // Note (Nick, 2026-01-13, REPL-22167): Creep toward 95% over ~10 seconds with easing
            const progressTime = Math.min(timeStuck - 500, 10000);
            const progressRatio = progressTime / 10000;
            // Note (Nick, 2026-01-13, REPL-22167): Ease out for smoother deceleration as it approaches ceiling
            const easedRatio = 1 - Math.pow(1 - progressRatio, 3);
            const ceiling = 95;
            const visualTarget = target + (ceiling - target) * easedRatio;
            effectiveTarget = Math.max(effectiveTarget, visualTarget);
            maxProgressRef.current = effectiveTarget;
          }
        }

        // Note (Nick, 2026-01-13, REPL-22167): Speed up when reaching 100% for snappy completion feel
        const speed = target === 100 ? 0.18 : baseSpeed;

        const next = lerp(v, effectiveTarget, speed);

        // Note (Nick, 2026-01-13, REPL-22167): Snap when we're <0.5% away to avoid interminable micro-steps
        if (Math.abs(next - effectiveTarget) < 0.5) {
          // Note (Nick, 2026-01-13, REPL-22167): Keep animating if we're creeping forward (target < 100)
          if (target < 100) {
            frame = requestAnimationFrame(animate);
          }
          return effectiveTarget;
        }

        frame = requestAnimationFrame(animate);
        return next;
      });
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target, baseSpeed]);

  return value;
};

interface LoadingHeaderProps {
  pageRoute: string;
}

const LoadingHeader: React.FC<LoadingHeaderProps> = ({ pageRoute }) => {
  const { phase, progress: targetProgress } = useLoadingPhase(pageRoute);

  const isFullyLoaded = phase === "ready";
  const [isHidden, setIsHidden] = useOverridableState(isFullyLoaded);
  const progress = useAnimatedProgress(targetProgress);

  React.useEffect(() => {
    if (isFullyLoaded) {
      const timeout = setTimeout(() => {
        setIsHidden(true);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isFullyLoaded, setIsHidden]);

  return (
    <div
      className={twMerge(
        "absolute top-0 left-0 right-0 overflow-hidden z-1",
        isHidden && "pointer-events-none",
      )}
    >
      <div
        className={twMerge(
          "bg-white flex flex-col items-stretch gap-3",
          "transition-transform duration-300",
          !isFullyLoaded && "pb-3 border-b border-border",
          isHidden && "-translate-y-full",
        )}
      >
        <Progress
          value={progress}
          UNSAFE_className="w-auto rounded-none bg-surface"
          UNSAFE_indicatorClassName="transition"
        />
        {!isFullyLoaded && (
          <div className="flex flex-row px-4 gap-2">
            <div className="flex flex-row items-center border-1 text-sky border-sky-border justify-center h-6 w-6 gap-2 bg-sky-soft rounded-md">
              <LoaderIcon
                size={12}
                className="animate-[spin_1.5s_linear_infinite]"
              />
            </div>
            <div>
              <div className="header-h6">Loading Your Page</div>
              <div className="text-sm text-muted">
                You'll be able to interact with your page soon.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingHeader;
